import React from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { PropTypes as PT } from 'prop-types';
import _compact from 'lodash/compact';
import _get from 'lodash/get';
import { ReactComponent as AudioIcon } from '@lifesize/ux-assets/vectors/svgIcons/headset.svg';
import { ReactComponent as ChatIcon } from '@lifesize/ux-assets/vectors/svgIcons/chat.svg';
import { ReactComponent as PeopleIcon } from '@lifesize/ux-assets/vectors/svgIcons/people.svg';
import { ReactComponent as OtmIcon } from '@lifesize/ux-assets/vectors/svgIcons/hourglass.svg';
import { ReactComponent as PersonIcon } from '@lifesize/ux-assets/vectors/svgIcons/person.svg';
import { ReactComponent as RoomSystemIcon } from '@lifesize/ux-assets/vectors/svgIcons/room-system.svg';
import { ReactComponent as ScheduleIcon } from '@lifesize/ux-assets/vectors/svgIcons/calendar.svg';
import { ReactComponent as VideoIcon } from '@lifesize/ux-assets/vectors/svgIcons/video.svg';
import { nucleusConstants } from '@lifesize/nucleus';
import { Tooltip } from '@material-ui/core';
import FavoriteStar from 'containers/FavoriteStar/FavoriteStarContainer';
import { currentUserCanChat } from 'utils/chat';
import classes from './ContactCardHeader.module.scss';

const calcOpacity = (scroll, startPosition, endPosition) => {
  const height = endPosition - startPosition;

  return height ? (endPosition - Math.max(Math.min(scroll, endPosition), startPosition)) / height : 1;
};

class ContactCardHeader extends React.Component {
  constructor(props) {
    super(props);

    this.descriptionHeight = 0;
  }

  componentDidMount() {
    this.descriptionHeight = this.descriptionElt ? this.descriptionElt.clientHeight : 0;
  }

  componentDidUpdate() {
    this.descriptionHeight = this.descriptionElt ? this.descriptionElt.clientHeight : 0;
  }

  onCallAudio = (event) => {
    event.stopPropagation();
    this.props.handleAudioCall(this.props.contact);
  };

  onCallVideo = (event) => {
    event.stopPropagation();
    this.props.handleVideoCall(this.props.contact);
  };

  onChat = (event) => {
    event.stopPropagation();
    this.props.handleChat(this.props.contact);
  };

  onRemoteModerate = (event) => {
    const extension = _get(this.props.contact, 'dialString') || _get(this.props.contact, 'extension');
    event.stopPropagation();

    if (this.props.remoteModerationIsActiveForThisMeeting) {
      this.props.handleRemoteModerate();
    } else if (this.props.remoteModerationIsActive) {
      this.props.handleRemoteModerate(extension, true);
    } else {
      this.props.handleRemoteModerate(extension);
    }
  };

  onSchedule = (event) => {
    event.stopPropagation();
    this.props.handleSchedule(this.props.contact.id);
  };

  getCollapseConfig() {
    const { scroll } = this.props;
    return {
      buttonBarClasses: _compact([classes.buttonBar, scroll > 137 ? classes.fixed : '']).join(' '),
      moderationBarClasses: _compact([classes.moderatorBar, scroll > 137 ? classes.fixed : '']).join(' '),
      descriptionStyle: { opacity: calcOpacity(scroll, 137, 137 + this.descriptionHeight) },
      titleStyle: { height: scroll > 118 ? 52 : 'auto' },
      iconStyle: { opacity: calcOpacity(scroll, 0, 118) },
      nameClasses: _compact([classes.contactName, scroll > 118 ? classes.fixed : '']).join(' '),
      presenceStyle: { opacity: calcOpacity(scroll, 118, 137) }
    };
  }

  renderMeetingDescription(descriptionStyle) {
    const { description, isMeeting } = this.props.contact;

    if (isMeeting && description && !this.props.remoteModerationIsActiveForThisMeeting) {
      return (
        <div
          className={classes.description}
          ref={(elt) => {
            this.descriptionElt = elt;
          }}
          style={descriptionStyle}
        >
          {description}
        </div>
      );
    }
    return null;
  }

  renderIcon = (cardConfig) => {
    const { contact } = this.props;
    let icon;

    if (this.props.remoteModerationIsActiveForThisMeeting) return null;

    if (contact.isRoomSystem) {
      icon = <RoomSystemIcon data-testid="roomSystemIcon" />;
    } else if (contact.isMeeting) {
      if (contact.meetingType === nucleusConstants.MEETING_TYPE_ONE_TIME) {
        icon = <OtmIcon data-testid="otmIcon" />;
      } else {
        icon = <PeopleIcon data-testid="peopleIcon" />;
      }
    } else {
      icon = <PersonIcon data-testid="personIcon" />;
    }

    return (
      <div className={classes.cardIcon} style={cardConfig.iconStyle}>
        {icon}
        <FavoriteStar favClassName={classes.favorites} contact={contact} />
      </div>
    );
  };

  renderTitle = (cardConfig) => {
    const { name } = this.props.contact;

    return (
      <div className={classes.contactNameContainer} style={cardConfig.titleStyle}>
        {name.length > 16 ? (
          <Tooltip title={name} placement="bottom" arrow={true}>
            <h3 className={cardConfig.nameClasses}>{name}</h3>
          </Tooltip>
        ) : (
          <h3 className={cardConfig.nameClasses}>{name}</h3>
        )}
      </div>
    );
  };

  renderRemoteModerationButton = (moderationBarClasses) => {
    const isActive = this.props.remoteModerationIsActiveForThisMeeting;
    if (!this.props.userCanRemoteModerate) return null;
    return (
      <div className={moderationBarClasses}>
        <button
          data-testid={isActive ? 'stopModerating' : 'startModerating'}
          className={isActive ? classes.active : ''}
          onClick={this.onRemoteModerate}
        >
          {isActive ? (
            <FormattedMessage id="components.inCall.stopRemoteModeration" defaultMessage="Stop Moderating" />
          ) : (
            <FormattedMessage id="components.inCall.joinAsRemoteModerator" defaultMessage="Join as Remote Moderator" />
          )}
        </button>
      </div>
    );
  };

  render() {
    const { contact } = this.props;
    const icons = [];

    if (contact.isCallable) {
      icons.push(
        <Tooltip
          key="1"
          title={<FormattedMessage id="contacts.tooltip.video" defaultMessage="Call with camera on" />}
          placement="bottom"
          arrow={true}
        >
          <button className={classes.cardButton} onClick={this.onCallVideo} data-testid="callButtonOn">
            <VideoIcon data-testid="videoIcon" />
          </button>
        </Tooltip>
      );
      icons.push(
        <Tooltip
          key="2"
          title={<FormattedMessage id="contacts.tooltip.audio" defaultMessage="Call with camera off" />}
          placement="bottom"
          arrow={true}
        >
          <button className={classes.cardButton} onClick={this.onCallAudio} data-testid="callButtonOff">
            <AudioIcon data-testid="audioIcon" />
          </button>
        </Tooltip>
      );
    } else {
      icons.push(
        <span className={`${classes.cardButton} ${classes.disabled}`} key="1" data-testid="callButtonDisabled">
          <VideoIcon data-testid="videoIcon" />
        </span>
      );
      icons.push(
        <span className={`${classes.cardButton} ${classes.disabled}`} key="2">
          <AudioIcon data-testid="audioIcon" />
        </span>
      );
    }

    // Room systems don't have chat
    // We are no longer passing this.props.currentUserSuperUser to currentUserCanChat
    if (
      contact.isChattable &&
      this.props.handleChat &&
      this.props.isChatEnabled &&
      currentUserCanChat(contact, this.props.currentUserUuid)
    ) {
      icons.push(
        <Tooltip
          key="3"
          title={<FormattedMessage id="contacts.tooltip.chat" defaultMessage="Chat" />}
          placement="bottom"
          arrow={true}
        >
          <button className={classes.cardButton} onClick={this.onChat} data-testid="chatButtonOn">
            <ChatIcon data-testid="contact-card-chat-icon" />
          </button>
        </Tooltip>
      );
    }

    if (this.props.handleSchedule) {
      icons.push(
        <Tooltip
          key="4"
          title={<FormattedMessage id="contacts.tooltip.schedule" defaultMessage="Schedule" />}
          placement="bottom"
          arrow={true}
        >
          <button className={classes.cardButton} onClick={this.onSchedule} data-testid="scheduleButtonOn">
            <ScheduleIcon data-testid="contact-card-schedule-icon" />
          </button>
        </Tooltip>
      );
    }

    let presenceText;
    switch (contact.presence) {
      case 'AVAILABLE':
        if (contact.isMeeting) {
          if (contact.participantsCount >= 1) {
            presenceText = (
              <FormattedMessage
                id="components.contact.participantsCount"
                defaultMessage="{count, plural, one {# participant} other {# participants}}"
                values={{
                  count: contact.participantsCount
                }}
              />
            );
          } else {
            presenceText = <FormattedMessage id="ready" defaultMessage="Ready" />;
          }
        } else {
          presenceText = <FormattedMessage id="components.contactCard.available" defaultMessage="Available" />;
        }
        break;
      case 'NOT_AVAILABLE':
        presenceText = <FormattedMessage id="components.contactCard.notAvailable" defaultMessage="Not Available" />;
        break;
      default:
        presenceText = <FormattedMessage id="components.contactCard.doNotDisturb" defaultMessage="Do Not Disturb" />;
        break;
    }

    let presence;
    switch (contact.presence) {
      case 'AVAILABLE':
        presence = <span className={classes.contactPresenceAvailable} />;
        break;
      case 'NOT_AVAILABLE':
        presence = <span className={classes.contactPresenceNotAvailable} />;
        break;
      default:
        presence = <span className={classes.contactPresenceDoNotDisturb} />;
    }

    const cardConfig = this.getCollapseConfig();

    const buttonBarContainerClass = () => {
      if (!this.props.remoteModerationIsActiveForThisMeeting && this.props.userCanRemoteModerate) {
        return classes.withButtonBarAndModeration;
      } else if (!this.props.remoteModerationIsActiveForThisMeeting && !this.props.userCanRemoteModerate) {
        return classes.withButtonBarOnly;
      }
      return classes.withModerationOnly;
    };

    return (
      <header className={classes.container}>
        {this.renderIcon(cardConfig)}
        {this.renderTitle(cardConfig)}
        <div className={classes.presenceText} style={cardConfig.presenceStyle} data-testid="contactCardPresence">
          {presence}
          {presenceText}
        </div>
        <div className={`${classes.buttonBarContainer} ${buttonBarContainerClass()}`}>
          {!this.props.remoteModerationIsActiveForThisMeeting && (
            <div className={cardConfig.buttonBarClasses}>{icons}</div>
          )}
          {this.renderRemoteModerationButton(cardConfig.moderationBarClasses)}
        </div>
        {this.renderMeetingDescription(cardConfig.descriptionStyle)}
      </header>
    );
  }
}

ContactCardHeader.propTypes = {
  contact: PT.object.isRequired,
  currentUserUuid: PT.string,
  currentUserSuperUser: PT.bool,
  handleAudioCall: PT.func,
  handleChat: PT.func,
  handleRemoteModerate: PT.func,
  handleSchedule: PT.func,
  handleVideoCall: PT.func,
  intl: PT.object,
  isChatEnabled: PT.bool,
  remoteModerationIsActive: PT.bool,
  remoteModerationIsActiveForThisMeeting: PT.bool,
  scroll: PT.number,
  userCanRemoteModerate: PT.bool
};

export default injectIntl(ContactCardHeader);
